import {
    Form,
    DatePicker,
    Select,
    Input,
    Button,
    Card,
    Typography,
    Breadcrumb,
    Modal,
    Table,
    Row,
    Col,
    TimePicker,
    Grid
} from "antd";
import { useState, useContext, useEffect } from "react";
import dayjs from "dayjs";
import { AuthContext } from "../../context/AuthContext";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { CloseOutlined } from "@ant-design/icons";
import "react-toastify/dist/ReactToastify.css";

const { TextArea } = Input;
const { Title } = Typography;
const { useBreakpoint } = Grid;

export default function Permission() {
    const [form] = Form.useForm();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { authData, backUrl } = useContext(AuthContext);
    const screens = useBreakpoint();
    const [submitting, setSubmitting] = useState(false);


    // ================= Submit Handler =================
    const onFinish = async (values) => {
        setSubmitting(true);
        try {
            const formattedValues = {
                ...values,
                date: values.date
                    ? dayjs(values.date).format("YYYY-MM-DD")
                    : null,
                time: values.time
                    ? dayjs(values.time).format("HH:mm")
                    : null,
            };

            const res = await axios.post(
                `${backUrl}/api/hr/permissions/`,
                formattedValues,
                {
                    headers: {
                        Authorization: `Bearer ${authData.access}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            toast.success(
                res?.data?.message ||
                "Permission Request Submitted Successfully!"
            );

            form.resetFields();
        } catch (error) {
            const errorMsg =
                error?.response?.data?.message ||
                error?.response?.data?.data ||
                "An error occurred while submitting your request.";

            toast.error(errorMsg);
        } finally {
            setSubmitting(false);
        }
    };


    // ================== Permission Record Subcomponent ==================
    const PermissionRecord = () => {
        const [filters, setFilters] = useState({
            fromDate: null,
            toDate: null,
            month: null,
            year: null,
            session: null,
            status: null,
        });

        const [permissionData, setPermissionData] = useState([]);
        const [loading, setLoading] = useState(false);
        const [isCancelModalVisible, setIsCancelModalVisible] = useState(false);
        const [cancelReason, setCancelReason] = useState("");
        const [selectedRecord, setSelectedRecord] = useState(null);
        const [refreshTrigger, setRefreshTrigger] = useState(0);

        const fetchPermissionData = async () => {
            try {
                setLoading(true);
                const res = await axios.get(
                    `${backUrl}/api/hr/permissions/my-permissions`,
                    {
                        headers: {
                            Authorization: `Bearer ${authData.access}`,
                            "Content-Type": "application/json",
                        },
                    }
                );

                const mappedData = res.data.results.map((item, index) => ({
                    key: item.id ?? index,
                    id: item.id,
                    permissionDate: dayjs(item.date).format("DD/MM/YYYY"),
                    session:
                        item.session === "FN"
                            ? "Forenoon"
                            : item.session === "AN"
                                ? "Afternoon"
                                : item.session,
                    reason: item.reason,
                    status: item.approve_status,
                    hours_requested: item.hours_requested,
                    approvals: item.approvals ?? [],
                    cancellation_reason: item.cancellation_reason,
                    created_by_system: item.created_by_system,
                    consideration_requests: item.attendance?.consideration_requests ?? [],
                    time: item.time
                        ? dayjs(item.time, ["HH:mm:ss", "HH:mm"]).format("HH:mm")
                        : null
                }));

                setPermissionData(mappedData);
            } catch (err) {
                console.error("Error fetching permission data:", err);
                toast.error("Failed to load permission records");
            } finally {
                setLoading(false);
            }
        };

        useEffect(() => {
            fetchPermissionData();
        }, [refreshTrigger]);

        const handleCancelPermission = async () => {
            if (!selectedRecord) return;
            try {
                await axios.post(
                    `${backUrl}/api/hr/permissions/${selectedRecord.id}/cancel/`,
                    { cancellation_reason: cancelReason },
                    {
                        headers: {
                            Authorization: `Bearer ${authData.access}`,
                            "Content-Type": "application/json",
                        },
                    }
                );
                toast.success("Permission Cancelled Successfully");
                setIsCancelModalVisible(false);
                setCancelReason("");
                setSelectedRecord(null);
                setRefreshTrigger(prev => prev + 1);
            } catch (error) {
                console.error("Error cancelling permission:", error);
                toast.error("Failed to cancel permission");
            }
        };

        const columns = [
            { title: "Permission Date", dataIndex: "permissionDate", key: "permissionDate", width: 150 },
            { title: "Session", dataIndex: "session", key: "session", width: 100 },
            { title: "Hours", dataIndex: "hours_requested", key: "hours_requested", width: 80 },
            {
                title: "Time",
                dataIndex: "time",
                key: "time",
                width: 80,
            },
            {
                title: "Reason",
                key: "reason",
                width: 400,
                render: (_, record) => (
                    <>
                        <div style={{ whiteSpace: "normal", wordWrap: "break-word", maxWidth: "400px" }}>
                            {record.reason}
                        </div>

                        {record.cancellation_reason && (
                            <div
                                style={{
                                    marginTop: "4px",
                                    fontWeight: 600,
                                    color: "red",
                                    backgroundColor: "#ffe6e6",
                                    padding: "3px 6px",
                                    borderRadius: "6px",
                                    display: "inline-block",
                                }}
                            >
                                Cancellation Reason: {record.cancellation_reason}
                            </div>
                        )}
                    </>
                ),
            },
            {
                title: "Status",
                dataIndex: "status",
                key: "status",
                render: (text) => (
                    <span
                        style={{
                            fontWeight: 500,
                            color:
                                text === "Approved"
                                    ? "green"
                                    : text === "Rejected"
                                        ? "red"
                                        : "#faad14",
                        }}
                    >
                        {text}
                    </span>
                ),
            },
            {
                title: "Remarks",
                key: "remarks",
                width: 400,
                render: (_, record) => {
                    const approvals = record.approvals || [];
                    const considerationRequests = record.consideration_requests || [];

                    if (approvals.length === 0) {
                        return <span style={{ color: "#999" }}>—</span>;
                    }

                    return (
                        <div style={{ whiteSpace: "normal", wordWrap: "break-word", maxWidth: "400px" }}>
                            {/* Direct Approvals */}
                            {approvals.map((a, index) => (
                                <div
                                    key={`approval-${index}`}
                                    style={{
                                        borderBottom: index < approvals.length - 1 ? "1px solid #ddd" : "none",
                                        paddingBottom: "6px",
                                        marginBottom: "6px",
                                    }}
                                >
                                    <strong>
                                        {a.approver?.user?.first_name || a.approver?.user?.last_name
                                            ? `${a.approver.user.first_name} ${a.approver.user.last_name}`
                                            : a.approver?.user?.username || "Unknown"}
                                        :
                                    </strong>{" "}
                                    <span style={{ color: "green" }}>{a.approver_reason}</span>
                                    <span style={{ color: "green" }}>{a.rejection_reason}</span>
                                </div>
                            ))}

                            {/* Consideration Requests */}
                            {/* {considerationRequests.map((cr, idx) => (
                                <div
                                    key={`consideration-${idx}`}
                                    style={{
                                        borderBottom: idx < considerationRequests.length - 1 ? "1px solid #ddd" : "none",
                                        paddingBottom: "6px",
                                        marginBottom: "6px",
                                    }}
                                >


                                    {cr.approvals?.length > 0 && (
                                        <div style={{ marginTop: "6px" }}>
                                            {cr.approvals.map((a, i) => (
                                                <>
                                                    <div key={i} style={{ marginLeft: "10px" }}>
                                                        <strong>{a.approver_name}:</strong>{" "}
                                                        <span style={{ color: "green" }}>
                                                            {a.remarks || a.status}
                                                        </span>
                                                    </div>
                                                    <hr />
                                                </>

                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))} */}

                        </div>
                    );
                },
            },
            {
                title: "Cancel",
                key: "cancel",
                width: 80,
                render: (_, record) => (
                    <Button
                        type="text"
                        danger
                        icon={<CloseOutlined />}
                        disabled={
                            record.status === "Rejected" ||
                            record.status === "Cancellation Approved" ||
                            record.status === "Pending Cancellation" ||
                            record.created_by_system === true
                        }
                        onClick={() => {
                            setSelectedRecord(record);
                            setIsCancelModalVisible(true);
                            setCancelReason("");
                        }}

                    />
                ),
            },
        ];

        const filteredData = permissionData.filter((item) => {
            const date = dayjs(item.permissionDate, "DD/MM/YYYY");
            const matchFrom = filters.fromDate ? date.isSameOrAfter(filters.fromDate, "day") : true;
            const matchTo = filters.toDate ? date.isSameOrBefore(filters.toDate, "day") : true;
            const matchMonth = filters.month ? date.month() + 1 === filters.month : true;
            const matchYear = filters.year ? date.year() === filters.year : true;
            const matchSession = filters.session ? item.session === filters.session : true;
            const matchStatus = filters.status
                ? item.status?.toLowerCase().includes(filters.status.toLowerCase())
                : true;
            const matchTime = filters.time
                ? dayjs(item.time, ["HH:mm:ss", "HH:mm"]).format("HH:mm") === filters.time
                : true;

            return (
                matchFrom &&
                matchTo &&
                matchMonth &&
                matchYear &&
                matchSession &&
                matchStatus &&
                matchTime
            );
        });
        const years = Array.from({ length: 11 }, (_, i) => 2020 + i);

        return (
            <>
                <Modal
                    title={`Cancel Permission (${selectedRecord?.permissionDate || "—"})`}
                    open={isCancelModalVisible}
                    onCancel={() => {
                        setIsCancelModalVisible(false);
                        setCancelReason("");
                        setSelectedRecord(null);
                    }}
                    footer={[
                        <Button
                            key="back"
                            onClick={() => {
                                setIsCancelModalVisible(false);
                                setCancelReason("");
                                setSelectedRecord(null);
                            }}
                        >
                            Close
                        </Button>,
                        <Button
                            key="submit"
                            type="primary"
                            danger
                            onClick={handleCancelPermission}
                            disabled={!cancelReason.trim()}
                        >
                            Submit
                        </Button>,
                    ]}
                >
                    <TextArea
                        rows={4}
                        placeholder="Enter cancellation reason (required)"
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                    />
                </Modal>

                {/* Filters */}
                <div
                    style={{
                        display: "flex",
                        flexWrap: "wrap",
                        justifyContent: "center",
                        gap: screens.xs ? 8 : 12,
                        marginBottom: 16,
                    }}
                >
                    <DatePicker placeholder="From Date" format="DD/MM/YYYY" style={{ width: screens.xs ? "100%" : 160 }} onChange={(date) => setFilters({ ...filters, fromDate: date })} />
                    <DatePicker placeholder="To Date" format="DD/MM/YYYY" style={{ width: screens.xs ? "100%" : 160 }} onChange={(date) => setFilters({ ...filters, toDate: date })} />
                    <Select placeholder="Month" style={{ width: screens.xs ? "100%" : 140 }} allowClear onChange={(v) => setFilters({ ...filters, month: v })}>
                        {Array.from({ length: 12 }, (_, i) => (
                            <Select.Option key={i + 1} value={i + 1}>
                                {dayjs().month(i).format("MMMM")}
                            </Select.Option>
                        ))}
                    </Select>
                    <Select placeholder="Year" style={{ width: screens.xs ? "100%" : 120 }} allowClear onChange={(v) => setFilters({ ...filters, year: v })}>
                        {years.map((year) => (
                            <Select.Option key={year} value={year}>
                                {year}
                            </Select.Option>
                        ))}
                    </Select>
                    <Select placeholder="Session" style={{ width: screens.xs ? "100%" : 160 }} allowClear onChange={(v) => setFilters({ ...filters, session: v })}>
                        <Select.Option value="Forenoon">Forenoon</Select.Option>
                        <Select.Option value="Afternoon">Afternoon</Select.Option>
                    </Select>
                    <Select placeholder="Status" style={{ width: screens.xs ? "100%" : 200 }} allowClear onChange={(v) => setFilters({ ...filters, status: v })}>
                        <Select.Option value="Approved">Approved</Select.Option>
                        <Select.Option value="Rejected">Rejected</Select.Option>
                        <Select.Option value="Pending Cancellation">Pending Cancellation</Select.Option>
                        <Select.Option value="Cancelled">Cancelled</Select.Option>
                    </Select>
                </div>

                {/* Table */}
                <Table
                    columns={columns}
                    dataSource={filteredData}
                    pagination={{ pageSize: 5 }}
                    bordered
                    loading={loading}
                    components={{
                        header: {
                            cell: (props) => (
                                <th {...props} style={{ backgroundColor: "#0e497b", color: "white" }}>
                                    {props.children}
                                </th>
                            ),
                        },
                    }}
                    scroll={{ x: screens.xs ? 900 : undefined }}
                />
            </>
        );
    };

    return (
        <>
            <ToastContainer position="top-right" autoClose={4000} />

            <Modal
                title=""
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                footer={null}
                width={screens.xs ? "95%" : screens.sm ? "90%" : 1300}
                style={{
                    top: screens.xs ? 20 : 50,
                    padding: screens.xs ? "0 8px" : "0",
                }}
                bodyStyle={{
                    maxHeight: "80vh",
                    overflowY: "auto",
                }}
            >
                <PermissionRecord />
            </Modal>

            <center>
                <div
                    className="flex flex-col items-center min-h-screen bg-gray-50 p-6"
                    style={{
                        padding: screens.xs ? "12px" : "24px",
                    }}
                >
                    <div className="w-full max-w-5xl mb-4">
                        <Breadcrumb
                            items={[
                                {
                                    title: (
                                        <span
                                            style={{ cursor: "pointer", color: "#0e497b" }}
                                            onClick={() => setIsModalOpen(true)}
                                        >
                                            View Permissions
                                        </span>
                                    ),
                                },
                            ]}
                        />
                    </div>

                    <Card
                        title={
                            <Title
                                level={4}
                                style={{ margin: 0, textAlign: "center", color: "#0e497b" }}
                            >
                                Permission Request
                            </Title>
                        }
                        style={{
                            width: screens.xs ? "100%" : screens.sm ? "90%" : 600,
                            maxWidth: 600,
                        }}
                        bordered
                    >
                        <Form form={form} layout="vertical" onFinish={onFinish}>
                            <Form.Item
                                label="Permission Date"
                                name="date"
                                rules={[{ required: true, message: "Please select the date!" }]}
                            >
                                <DatePicker
                                    style={{ width: "100%" }}
                                    format="DD/MM/YYYY"
                                    disabledDate={(current) =>
                                        current &&
                                        (current < dayjs(authData?.employee?.date_of_joining, "YYYY-MM-DD") ||
                                            current.day() === 0)
                                    }
                                    dateRender={(current) => {
                                        const style = {};
                                        if (current.day() === 0) {
                                            style.color = "red";
                                            style.fontWeight = "bold";
                                        }
                                        return (
                                            <div className="ant-picker-cell-inner" style={style}>
                                                {current.date()}
                                            </div>
                                        );
                                    }}
                                />
                            </Form.Item>

                            <Form.Item
                                label="Session"
                                name="session"
                                rules={[{ required: true, message: "Please select a session!" }]}
                            >
                                <Select placeholder="Select session">
                                    <Select.Option value="FN">Forenoon</Select.Option>
                                    <Select.Option value="AN">Afternoon</Select.Option>
                                </Select>
                            </Form.Item>

                            <Row gutter={[16, 16]}>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label="Permission Duration"
                                        name="hours_requested"
                                        rules={[{ required: true, message: "Please select the Duration!" }]}
                                    >
                                        <Select placeholder="Select duration" style={{ width: "100%" }}>
                                            <Select.Option value="1">1 Hour</Select.Option>
                                            <Select.Option value="2">2 Hours</Select.Option>
                                        </Select>
                                    </Form.Item>
                                </Col>

                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label="Permission Time"
                                        name="time"
                                        rules={[{ required: true, message: "Please enter the time!" }]}
                                    >
                                        <TimePicker format="HH:mm" style={{ width: "100%" }} use12Hours={false} />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Form.Item
                                label="Reason"
                                name="reason"
                                rules={[{ required: true, message: "Please enter the reason!" }]}
                            >
                                <TextArea rows={4} placeholder="Enter your reason here..." />
                            </Form.Item>

                            <Form.Item>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={submitting}
                                    disabled={submitting}
                                    style={{
                                        width: "100%",
                                        backgroundColor: "#0e497b",
                                    }}
                                >
                                    {submitting ? "Submitting..." : "Request Permission"}
                                </Button>
                            </Form.Item>

                        </Form>
                    </Card>
                </div>
            </center>
        </>
    );
}
